import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * Field — a labelled read-only value in a card (deskkit.field).
 *
 * This is the design's display field, not an input: the canvases show a value
 * with an overline label, and editing happens by opening an editor. Passing
 * `onEdit` makes the whole card the control.
 */
export function Field({
  label,
  value,
  onEdit,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  onEdit?: () => void;
  className?: string;
}) {
  const body = (
    <>
      <p className="t-ovl mut">{label}</p>
      <p className="t-strong mt4">{value}</p>
    </>
  );

  if (!onEdit) {
    return <div className={clsx('card', className)}>{body}</div>;
  }
  return (
    <button type="button" className={clsx('card', 'w-full text-start', className)} onClick={onEdit}>
      {body}
    </button>
  );
}

/**
 * ToggleRow — a labelled switch in a card (deskkit.toggle_row).
 *
 * The kit gives the switch two captions, one per state, so a setting explains
 * its consequence rather than just its name — "results reach the patient" vs
 * "results wait for your release". Both are optional; pass neither for a bare
 * switch.
 *
 * The kit also carries a `tgl-off` marker class, which exists only so the
 * STATIC grid artboards can render an off switch (they freeze every binding to
 * "on"). That is artboard apparatus with no meaning in a live app, so it is not
 * ported — here the switch simply reflects `checked`.
 */
export function ToggleRow({
  label,
  checked,
  onChange,
  captionOn,
  captionOff,
  disabled = false,
  className,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  captionOn?: ReactNode;
  captionOff?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const caption = checked ? captionOn : captionOff;

  return (
    <div className={clsx('card', className)}>
      <label className="flex" style={{ gap: 12, alignItems: 'center', cursor: disabled ? 'default' : 'pointer' }}>
        <div className="grow">
          <p className="t-strong">{label}</p>
          {caption ? <p className="t-cap mut">{caption}</p> : null}
        </div>
        {/* A real checkbox carries the state and the keyboard behaviour; the
            kit's .tgl is the visual. Hiding the input visually rather than with
            display:none keeps it focusable and announced. */}
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <span className={clsx('tgl', checked && 'on')} aria-hidden="true" />
      </label>
    </div>
  );
}
